import { ChangeDetectionStrategy, Component, input, computed } from '@angular/core';
import { OrganizationStatus } from '../../../core/models/organization.models';
import {
  getOrganizationStatusHint,
  getOrganizationStatusLabel,
} from '../../utils/organization-status.utils';
import { getStatusPillClassFromTone, getStatusTone } from '../../utils/status-pill.utils';
import { HintComponent } from '../hint/hint.component';
import { OrganizationStatus as OrgStatusConst } from '../../../core/constants/status.constants';
import { getStatusLabel } from '../../maps/labels/status-labels.map';

@Component({
  selector: 'app-status-badge',
  imports: [HintComponent],
  templateUrl: './status-badge.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StatusBadge {
  readonly status = input<OrganizationStatus | null>(null);
  readonly showHint = input<boolean>(true);
  readonly statusPillClass = getStatusPillClassFromTone;
  readonly showVersion = computed(() => this.status()?.version != null);

  readonly hasContent = computed(() => {
    return this.showVersion() || this.showOrgStatus() || this.statusCountBadges().length > 0;
  });

  readonly showOrgStatus = computed(() => {
    const lastStatus = this.status()?.last_status;
    if (!lastStatus) return false;
    return (
      lastStatus === OrgStatusConst.WAITING_TO_BE_SIGNED || lastStatus === OrgStatusConst.COMPLETED
    );
  });

  readonly statusTone = computed(() => getStatusTone(this.status()?.last_status));

  readonly versionLabel = () => {
    const version = this.status()?.version;
    return version ? `v${version}` : '';
  };

  readonly statusLabel = () => {
    return getOrganizationStatusLabel(this.status()?.last_status);
  };

  readonly statusHint = () => getOrganizationStatusHint(this.status()?.last_status);

  readonly statusCountBadges = computed(() => {
    const counts = this.status()?.status_counts;
    if (!counts) return [];

    return Object.entries(counts)
      .filter(([, count]) => count > 0)
      .map(([status, count]) => ({
        status,
        count,
        label: getStatusLabel(status),
        tone: getStatusTone(status),
      }));
  });
}
